import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    ViewChild,
    ViewEncapsulation,
    Input,
    signal,
    computed
} from "@angular/core";

import {LockTemplateDependencyTreeService} from "./lock-template-dependency-tree.service";
import {NestedTreeControl} from '@angular/cdk/tree';
import {
    MatTreeNestedDataSource,
} from '@angular/material/tree';
import {LockTemplateVersion, LockVersionDependencyNode} from '../../_models/lock-template-version';
import {catchError, first, map, takeUntil, tap} from 'rxjs/operators';
import {forkJoin, Observable, of} from 'rxjs';
import {ModelID} from '../../_typing/generic-types';
import { HttpClient } from "@angular/common/http";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {BaseComponent} from '../../shared/base.component';
import {
    CONSTANT_PROPERTY_AGGREGATIONS,
    ConstantProperty,
    ConstantPropertyAggregation,
    isConstantProperty
} from '../../_models/constant-property';
import {SearchQueryOptions} from '../../services/api/search-query-options';
import {COMMON_MAT_TREE_IMPORTS, COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import { CommonModule, NgTemplateOutlet } from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatError} from "@angular/material/form-field";
import {MatDialog} from "@angular/material/dialog";
import {SharedPipesModule} from "../../shared/modules/shared-pipes.module";
import {ApiService} from '../../services/api/api.service';
import {UserService} from '../../services/user.service';
import {NotificationService} from '../../services/notification.service';
import {ConfirmDialogComponent, ConfirmDialogData} from '../../forms/confirm-dialog/confirm-dialog.component';

/**
 * Tracks which container nodes were expanded across a tree refetch, keyed on the stable
 * component-type or event-type id a container carries rather than on node.id, the traversal
 * counter LockTemplateDependencyTreeService.extractNode reassigns from scratch on every rebuild.
 */
interface TreeExpansionState {
    expanded: Set<string>;
    known: Set<string>;
}

/** Snapshot of the three edited attributes as they stood when the form was last loaded from the server. */
interface LoadedConstantPropertyValues {
    name_formula: string;
    aggregation: ConstantPropertyAggregation | null;
    description: string | null;
    changed_on: string | null;
}

@Component({
    selector: 'lock-template-dependency-tree',
    templateUrl: 'lock-template-dependency-tree.component.html',
    styleUrls: ['../../tables/lock-template-version-table/lock-template-version-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [LockTemplateDependencyTreeService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        NgTemplateOutlet,
        COMMON_MAT_TREE_IMPORTS,
        COMMON_FORM_IMPORTS,
        ReactiveFormsModule,
        MatIcon,
        MatIconButton,
        MatError,
        SharedPipesModule
    ],
})

export class LockTemplateDependencyTree extends BaseComponent {
    selected: string[] = [];
    prop_expanded = {};
    _template_version: LockTemplateVersion;
    @Input() set template_version(template_version: LockTemplateVersion) {
        if (template_version && template_version?.id !== this._template_version?.id) {
            this._template_version = template_version;
            let tree = this.cps.generateTreeSchema(this.template_version.attributes.schema);
            this.loadTree(tree);
        }
    }

    get template_version(): LockTemplateVersion {
        return this._template_version;
    }

    @Input() component_type_id: ModelID;
    @Input() event_type_id: ModelID;
    @Input() constant_property_id: ModelID;
    @Input() selected_constant_property: ConstantProperty;
    @Input() allow_edit = false;

    editing_node_id = signal<ModelID | null>(null);
    editing_property: ConstantProperty | null = null;
    loading_property = signal(false);
    saving = signal(false);
    /**
     * The three attributes the inline form edits. name_formula's required validator is applied
     * only while the fetched property is itself a calculation, since a non-calculation property
     * legitimately carries no formula; see setFormulaValidators.
     */
    readonly form = new FormGroup({
        description: new FormControl<string | null>(null),
        aggregation: new FormControl<ConstantPropertyAggregation | null>(null),
        name_formula: new FormControl<string | null>(null),
    });
    aggregations = CONSTANT_PROPERTY_AGGREGATIONS;
    /** Mirrors the form's live value as a signal, since computed() cannot read an RxJS stream directly. */
    private readonly form_value = signal(this.form.getRawValue());
    private readonly loaded_values = signal<LoadedConstantPropertyValues | null>(null);
    /**
     * True while a staged change is one the dirty_model trigger will flag. description sits in
     * that trigger's ignore list, so editing it alone queues nothing.
     */
    readonly recalculation_pending = computed(() => {
        const loaded = this.loaded_values();
        if (!loaded) {
            return false;
        }
        const current = this.form_value();
        return current.name_formula !== loaded.name_formula || current.aggregation !== loaded.aggregation;
    });
    /**
     * True when the open form holds a change the user actually made. description sits outside
     * recalculation_pending's trigger-aligned check but is still a real edit, so both are
     * compared here to decide whether closing the form needs confirmation.
     */
    private readonly hasUnsavedEdits = computed(() => {
        const loaded = this.loaded_values();
        if (!loaded) {
            return false;
        }
        return this.recalculation_pending() || this.form_value().description !== loaded.description;
    });
    private affected_type_names: string[] = [];
    private edit_request_seq = 0;
    private reload_seq = 0;

    private api = '/api/constant_property_calc_tree/';

    @ViewChild('lockTree') lockTree: { treeControl: { collapseAll: () => void; expandAll: () => void; }; };
    @ViewChild('firstFormField') firstFormField?: ElementRef<HTMLElement>;
    isExpanded = false;
    search_string: string = '';
    treeControl = new NestedTreeControl((node) => node['children']);
    dataSource = new MatTreeNestedDataSource();

    hasChild = (_: number, node: any) => !!node.children && node.children.length > 0;

    /**
     * Resolved once in ngOnInit rather than read live on every template check, the way
     * GenericConstantFieldComponent caches can_unlock: userService.canEditConstantProperty()
     * does a lookup on every call, and OnPush plus a template-bound method call on every check
     * would repeat it far more often than the permission can plausibly change within one open
     * dialog session.
     */
    can_edit = false;

    constructor(public cps: LockTemplateDependencyTreeService,
                private http: HttpClient,
                private api_service: ApiService,
                private userService: UserService,
                private notification: NotificationService,
                private dialog: MatDialog,
                private changeDetectorRef: ChangeDetectorRef,
                private elementRef: ElementRef<HTMLElement>) {
        super();
        this.form.valueChanges.pipe(takeUntil(this.onDestroy))
            .subscribe(() => this.form_value.set(this.form.getRawValue()));
    }

    editProperty(node) {
        // A save in flight owns the form until its response lands. Opening another node here
        // would retire the save's sequence number and discard its response unreported.
        if (!this.can_edit || this.saving()) {
            return;
        }
        if (this.editing_node_id() != null && this.editing_node_id() !== node.id && this.hasUnsavedEdits()) {
            this.confirmDiscard(() => this.beginEdit(node));
            return;
        }
        this.beginEdit(node);
    }

    /**
     * The Cancel button, and Escape, both funnel through here rather than tearing the form down
     * directly, so a genuine edit is never lost to a stray keypress or a misclick on another
     * node's Edit button.
     */
    cancelEdit() {
        if (this.hasUnsavedEdits()) {
            this.confirmDiscard(() => this.closeEditForm());
            return;
        }
        this.closeEditForm();
    }

    onFormEscape(event: KeyboardEvent): void {
        // This form sits inside a tree inside a dialog; without stopping propagation, Escape
        // would close the surrounding dialog instead of just this inline edit.
        event.stopPropagation();
        this.cancelEdit();
    }

    /**
     * Re-fetches the property the form is open on after a 409, since the conflict means the
     * copy on screen is no longer the row on the server. Goes through beginEdit so the fetch
     * carries the same sequence-number guard as any other open, and so the conflict error
     * clears the same way any fresh open clears it.
     */
    reloadAfterConflict(): void {
        if (this.editing_node_id() == null) {
            return;
        }
        this.beginEdit({id: this.editing_node_id()});
    }

    /**
     * Runs `onConfirm` only once the user has explicitly chosen to discard whatever is
     * currently unsaved, through the shared confirm dialog rather than a bespoke prompt.
     */
    private confirmDiscard(onConfirm: () => void): void {
        const data: ConfirmDialogData = {
            message: 'This constant property has unsaved changes. Discard them?',
            okText: 'Discard',
            cancelText: 'Keep editing',
        };
        this.dialog.open(ConfirmDialogComponent, {
            data,
            panelClass: 'lock-template-discard-dialog',
        })
            .afterClosed()
            .pipe(first(), takeUntil(this.onDestroy))
            .subscribe(confirmed => {
                if (confirmed) {
                    onConfirm();
                }
                this.changeDetectorRef.markForCheck();
            });
    }

    private beginEdit(node) {
        this.editing_node_id.set(node.id);
        this.editing_property = null;
        this.loading_property.set(true);
        this.loaded_values.set(null);
        // The FormGroup is reused across nodes and across a failed save, so opening any edit -
        // fresh or a post-conflict reload - starts from a clean slate rather than carrying over
        // another node's disabled state, touched state, or a previous save's conflict error.
        this.form.enable({emitEvent: false});
        this.form.reset();
        this.form.setErrors(null);
        // Cleared here, alongside the rest of this node's state, so the notice falls back to
        // non-enumerating wording until this node's own lookup resolves, rather than naming a
        // type that belongs to whatever property was open before this call.
        this.affected_type_names = [];

        // A response is only accepted when it belongs to the most recently issued call;
        // a call superseded by a later one (whether for the same node or a different one)
        // must not touch state.
        const requestedSeq = ++this.edit_request_seq;
        this.changeDetectorRef.markForCheck();

        const options = new SearchQueryOptions();
        options.filters = [{name: 'id', op: 'eq', val: node.id}];

        // `silent`: canEditConstantProperty is deliberately permissive across accounts, so a
        // refused GET is an expected outcome of showing the Edit button, not a surprise. This
        // handler already reports every failing status on its own; without `silent` the shared
        // HttpErrorHandler would toast the same failure a second time for some statuses.
        this.api_service.constant_property.searchSingle(options, true)
            .pipe(first(), takeUntil(this.onDestroy))
            .subscribe({
                next: (result: any) => {
                    if (requestedSeq !== this.edit_request_seq) {
                        return;
                    }
                    this.loading_property.set(false);
                    if (!result?.data) {
                        this.notification.openError('Could not load this constant property.');
                        this.closeEditForm();
                        return;
                    }
                    this.editing_property = result.data;
                    const attrs = result.data.attributes;
                    this.loaded_values.set({
                        name_formula: attrs.name_formula,
                        aggregation: attrs.aggregation,
                        description: attrs.description,
                        changed_on: attrs.changed_on,
                    });
                    this.form.reset({
                        description: attrs.description,
                        aggregation: attrs.aggregation,
                        name_formula: attrs.name_formula,
                    });
                    this.setFormulaValidators(attrs.is_calculation);
                    this.resolveAffectedTypeNames(result, requestedSeq);
                    this.changeDetectorRef.markForCheck();
                    this.focusFirstFormField();
                },
                error: () => {
                    if (requestedSeq !== this.edit_request_seq) {
                        return;
                    }
                    this.loading_property.set(false);
                    this.notification.openError('Could not load this constant property.');
                    this.closeEditForm();
                }
            });
    }

    /**
     * A non-calculation property legitimately carries no formula, so the required validator is
     * applied only while the fetched property is itself a calculation.
     */
    private setFormulaValidators(is_calculation: boolean): void {
        const control = this.form.controls.name_formula;
        control.setValidators(is_calculation ? Validators.required : null);
        control.updateValueAndValidity({emitEvent: false});
    }

    /**
     * Tears the form down and returns focus to the Edit button that opened it, whether that is
     * a genuine cancel, a failed fetch, or the close that follows a successful save. Cancelling
     * also retires the outstanding fetch, so no response can write into a closed form. The node
     * id is read before it is cleared, and used to find the button after it re-renders: the
     * button the user actually clicked no longer exists once this node's own @if has toggled
     * away and back, since Angular tears down and recreates that branch rather than reusing it.
     */
    private closeEditForm() {
        const closedNodeId = this.editing_node_id();
        this.edit_request_seq++;
        this.editing_node_id.set(null);
        this.editing_property = null;
        this.loading_property.set(false);
        this.loaded_values.set(null);
        this.affected_type_names = [];
        this.form.enable({emitEvent: false});
        this.form.reset();
        this.form.setErrors(null);
        this.changeDetectorRef.markForCheck();
        this.returnFocusToEditButton(closedNodeId);
    }

    private focusFirstFormField(): void {
        // The field this targets is behind the @if that editing_property just became truthy
        // for. Deferring via queueMicrotask is not enough: dispatching the click that reaches
        // this point runs inside a zone.js task, and zone.js drains the microtask queue before
        // that task returns control to its caller, which is earlier than the change detection
        // this method needs. Forcing it here, synchronously, guarantees the field exists first.
        this.changeDetectorRef.detectChanges();
        this.firstFormField?.nativeElement?.focus();
    }

    private returnFocusToEditButton(nodeId: ModelID | null): void {
        if (nodeId == null) {
            return;
        }
        // Looked up fresh rather than held from the click, for the same reason as the comment
        // on closeEditForm: the button this targets is recreated, not reused, once this node's
        // own Edit button reappears.
        queueMicrotask(() => {
            const button = this.elementRef.nativeElement
                .querySelector<HTMLElement>(`.cp-edit-button[data-cp-node-id="${nodeId}"]`);
            button?.focus();
        });
    }

    /**
     * Resolves the name of every component type and event type this property is attached to
     * through the gated type endpoints rather than a sideload, so a user who may edit this
     * property but may not view its attached types is never handed them. `affected_type_names`
     * is written only when every linked id resolves to a name; a refused lookup, a partial
     * resolution, or no linkage at all all leave it empty, and the notice falls back to
     * non-enumerating wording. Guarded by `requestedSeq` so a lookup that lands after the user
     * has cancelled or moved to another node cannot write into a form that has moved on.
     */
    private resolveAffectedTypeNames(result: any, requestedSeq: number): void {
        const componentTypeIds: string[] =
            (result?.data?.relationships?.component_types?.data || []).map(link => link.id);
        const eventTypeIds: string[] =
            (result?.data?.relationships?.event_types?.data || []).map(link => link.id);
        const linkedCount = componentTypeIds.length + eventTypeIds.length;

        if (!linkedCount) {
            this.affected_type_names = [];
            return;
        }

        forkJoin([
            this.searchTypeNames(this.api_service.component_type, componentTypeIds, 'component_type'),
            this.searchTypeNames(this.api_service.event_type, eventTypeIds, 'event_type'),
        ]).pipe(first(), takeUntil(this.onDestroy)).subscribe(([componentTypeNames, eventTypeNames]) => {
            if (requestedSeq !== this.edit_request_seq) {
                return;
            }
            const names = [...componentTypeNames, ...eventTypeNames];
            this.affected_type_names = names.length === linkedCount ? names : [];
            this.changeDetectorRef.markForCheck();
        });
    }

    /**
     * A refused (403) or otherwise failed lookup resolves to no names rather than propagating
     * an error, so a missing view permission on the linked type never raises a toast of its own
     * and never stops the property being edited; the caller treats it the same as any other
     * partial resolution. `silent` keeps the shared HttpErrorHandler from toasting that refusal
     * itself, since it is not something the user editing a formula needs to see. A 403 is
     * expected here and stays silent end to end; any other failure degrades the same way for
     * the user but is written to the console so it stays traceable.
     */
    private searchTypeNames(model: {searchMany: (options: SearchQueryOptions, silent?: boolean) => Observable<any>},
                             ids: string[], label: string): Observable<string[]> {
        if (!ids.length) {
            return of([]);
        }
        const options = new SearchQueryOptions();
        options.filters = [{name: 'id', op: 'in', val: ids}];
        return model.searchMany(options, true).pipe(
            map(response => (response?.data || []).map(item => item?.attributes?.name).filter(name => !!name)),
            catchError(error => {
                if (error?.status !== 403) {
                    console.error(`searchTypeNames(${label}) failed:`, error);
                }
                return of([]);
            })
        );
    }

    get recalculation_notice(): string {
        const scope = this.affected_type_names.length
            ? this.affected_type_names.join(', ')
            : 'the types this property is attached to';
        return `Saving queues a recalculation for every instance of ${scope}, and for everything `
            + 'downstream of this property. The work runs in the background.';
    }

    saveProperty() {
        const property = this.editing_property;
        if (!property || this.saving()) {
            return;
        }

        const formulaControl = this.form.controls.name_formula;
        if (property.attributes.is_calculation && !formulaControl.value) {
            // Surfaced on the field itself rather than as a toast: the field is the thing that is
            // wrong. Material shows its error subscript off the control's touched state, so a save
            // attempt marks the control the way leaving the field would.
            formulaControl.markAsTouched();
            this.changeDetectorRef.markForCheck();
            return;
        }

        // The server applies supplied attributes as a replace, so anything sent that the form
        // does not own is written back at its fetched value. The payload therefore carries the
        // three edited attributes and nothing else, and no relationships: linkage is a
        // full-collection replace on the server side. expected_changed_on is write-only and
        // carries the value fetched with the property; the server treats its absence as "no
        // precondition", so sending it is what turns its optimistic-concurrency check on.
        const formValue = this.form.getRawValue();
        const payload: any = {
            id: property.id,
            type: property.type,
            attributes: {
                description: formValue.description,
                aggregation: formValue.aggregation,
                name_formula: formValue.name_formula,
                expected_changed_on: this.loaded_values()?.changed_on,
            },
        };

        // Read before the request goes out: closeEditForm() (called from the success handler)
        // clears loaded_values, which recalculation_pending depends on.
        const queuesRecalculation = this.recalculation_pending();
        this.saving.set(true);
        this.form.disable({emitEvent: false});
        this.form.setErrors(null);
        // Captured rather than bumped: a save acts on the form that is already open rather than
        // superseding it, so the response must be checked against whatever is current when it
        // lands, without treating the save itself as something later work needs to supersede.
        const requestedSeq = this.edit_request_seq;
        this.api_service.constant_property.obsPatch(payload)
            .pipe(first(), takeUntil(this.onDestroy))
            .subscribe({
                next: () => {
                    this.saving.set(false);
                    if (requestedSeq !== this.edit_request_seq) {
                        // The form moved on to a different node while this save was in flight;
                        // tearing it down now would discard whatever is open there instead.
                        return;
                    }
                    this.notification.openSuccess(queuesRecalculation
                        ? 'Saved. Recalculation queued.'
                        : 'Constant property saved.');
                    this.closeEditForm();
                    this.reloadCalcTree();
                },
                error: (error) => {
                    this.saving.set(false);
                    if (requestedSeq !== this.edit_request_seq) {
                        return;
                    }
                    // Re-enabled before the status-specific branches below, since every one of
                    // them leaves the node in edit mode: enable() revalidates the group, which
                    // would otherwise wipe out a conflict error set after it rather than before.
                    this.form.enable({emitEvent: false});
                    if (error?.status === 409) {
                        // HttpErrorHandler.checkErrors already toasts a 409 unconditionally, so
                        // this only keeps the form open with the user's text and offers a
                        // reload; it must not toast a second time, and must not report a
                        // recalculation that a rejected write never actually queued.
                        this.form.setErrors({conflict: true});
                        return;
                    }
                    // HttpErrorHandler.checkErrors toasts 400 and 405 unconditionally, toasts
                    // 403 only when the response carries a JSONAPI detail, and otherwise (404,
                    // 500, and every other status) only logs to the console. This gate mirrors
                    // that split so the component reports exactly the statuses HttpErrorHandler
                    // leaves silent, and never both.
                    const reported = error?.status === 400 || error?.status === 405
                        || (error?.status === 403 && !!error?.error?.errors?.[0]?.detail);
                    if (reported) {
                        return;
                    }
                    this.notification.openError(error?.status === 403
                        ? 'You are not allowed to save this change. Your text has been kept.'
                        : 'The constant property could not be saved. Your text has been kept.');
                    // The node stays in edit mode so a rejected formula is fixable in place.
                }
            });
    }

    ngOnInit() {
        // See the field comment on can_edit: resolved once here rather than read live on every
        // template check, since allow_edit is set once per dialog session by its caller.
        this.can_edit = this.allow_edit && this.userService.canEditConstantProperty();

        this.cps.highlightNodes.pipe(
            takeUntil(this.onDestroy),
            tap(result => {
                this.selected = result;
                if (this.constant_property_id && this.selected?.length < 1) {
                    this.selected = [this.constant_property_id]
                }
                this.changeDetectorRef.markForCheck();
            })).subscribe();

        if (this.component_type_id && this.constant_property_id) {
            this.api += 'component/';
            this.loadCalcTree(this.component_type_id);
        } else if (this.event_type_id && this.constant_property_id) {
            this.api += 'event/';
            this.loadCalcTree(this.event_type_id);
        }

        if (this.constant_property_id) {
            this.selected = [this.constant_property_id]
        }
        if (this.selected_constant_property && isConstantProperty(this.selected_constant_property)) {
            this.search_string = this.selected_constant_property.attributes?.name;
        }
    }

    loadCalcTree(type_id: ModelID, preserve?: TreeExpansionState) {
        // Two saves in quick succession queue two reloads. Without this the earlier response
        // can land last and redraw the tree as it stood before the most recent save.
        const requestedSeq = ++this.reload_seq;
        this.http.get(this.api + type_id + '/' + this.constant_property_id)
            .pipe(takeUntil(this.onDestroy))
            .subscribe({
                next: (result: { data: LockVersionDependencyNode }) => {
                    if (requestedSeq !== this.reload_seq) {
                        return;
                    }
                    let tree = this.cps.generateTreeSchema(result.data);
                    this.loadTree(tree, preserve);
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    if (requestedSeq !== this.reload_seq) {
                        return;
                    }
                    // `preserve` is only ever passed by reloadCalcTree, after a save has
                    // already drawn a tree that this failed refresh leaves in place. The
                    // first load (ngOnInit, no `preserve`) has drawn nothing yet, so there is
                    // no stale tree to warn about; report that the load itself failed instead.
                    this.notification.openError(preserve
                        ? 'The dependency tree could not be refreshed. It may be out of date.'
                        : 'The dependency tree could not be loaded.');
                }
            });
    }

    loadTree(tree, preserve?: TreeExpansionState) {
        this.dataSource.data = [tree];
        this.treeControl.dataNodes = [tree];
        if (preserve) {
            this.restoreExpansion([tree], preserve);
            this.isExpanded = preserve.expanded.size > 0;
        } else {
            this.isExpanded = false;
            if (this.lockTree) {
                this.toggleFullTree();
            }
        }
        this.cps.findNodesFromFilter(this.search_string);
    }

    /**
     * A formula edit can change the dependency graph the tree draws, so a successful save
     * redraws it from the server rather than patching the node in place, carrying forward
     * whatever the user had expanded or collapsed while diagnosing.
     */
    private reloadCalcTree() {
        const typeId = this.component_type_id || this.event_type_id;
        if (!typeId || !this.constant_property_id) {
            return;
        }
        this.loadCalcTree(typeId, this.captureExpansionState());
    }

    private captureExpansionState(): TreeExpansionState {
        const state: TreeExpansionState = {expanded: new Set<string>(), known: new Set<string>()};
        this.walkContainers(this.dataSource.data as any[], (node, key) => {
            if (!key) {
                return;
            }
            state.known.add(key);
            if (this.treeControl.isExpanded(node)) {
                state.expanded.add(key);
            }
        });
        return state;
    }

    private restoreExpansion(nodes: any[], state: TreeExpansionState) {
        this.walkContainers(nodes, (node, key) => {
            // A container missing from the old tree, or one with no stable identity to compare,
            // is treated as new: it opens so the change the user just made is visible rather
            // than hidden.
            if (!key || state.expanded.has(key) || !state.known.has(key)) {
                this.treeControl.expand(node);
            }
        });
    }

    /** Constant-property nodes are leaves, so only nodes with children carry expansion state. */
    private walkContainers(nodes: any[], visit: (node: any, key: string | null) => void,
                           prefix = '') {
        (nodes || []).forEach(node => {
            if (!node?.children?.length) {
                return;
            }
            const key = this.containerKey(node, prefix);
            visit(node, key);
            this.walkContainers(node.children, visit, key ?? prefix);
        });
    }

    /**
     * extractNode sets exactly one of component_type_id or event_type_id on every container it
     * builds, and that id is stable across a rebuild where node.id, a per-traversal counter, is
     * not. The ancestor path is folded in so one type reached by two paths in the same tree
     * holds an expansion state per position rather than one shared between them. A container
     * carrying neither id has no stable identity and returns null, so it is treated as new on
     * every rebuild rather than sharing a key with every other such container.
     */
    private containerKey(node: any, prefix: string): string | null {
        const id = node?.component_type_id ?? node?.event_type_id;
        if (id == null) {
            return null;
        }
        const own = `${node.type}:${id}`;
        return prefix ? `${prefix}/${own}` : own;
    }

    toggleFullTree() {
        if (this.isExpanded) {
            this.lockTree.treeControl.collapseAll();
            this.isExpanded = false;
        } else {
            this.lockTree.treeControl.expandAll();
            this.isExpanded = true;
        }
    }
}
